import Bluebird from "bluebird";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { assertNativeError, ensureArray } from "@examspark/common";
import {
  Button,
  Callout,
  Modal,
  ModalProps,
  SubmitButtons,
  VSpace,
} from "@examspark/common-ui";
import {
  IconDownloadLine,
  IconTestTubeLine,
  IconWarningTriangleLine,
} from "@examspark/common-ui/dist/components/icons";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { ControllerContextInput, FeatureFlags } from "@examspark/types";

import { PrintHandlerResponse, printHandler } from "../utils/print";

const ButtonGroup = styled.div`
  display: flex;
  & ${Button} {
    margin-right: 2rem;
  }
`;

export type PrintModalRequest = ControllerContextInput<any> & {
  label?: string;
};

type Props = Omit<ModalProps, "onAfterClose" | "buttons"> & {
  requestPrintDisabled?: boolean;
  onRequestPrint?: () => PrintModalRequest[] | PrintModalRequest;
};

const PrintModal = ({
  requestPrintDisabled,
  onRequestPrint,
  children,
  ...props
}: Props) => {
  const flags = useFlags<Pick<FeatureFlags, "devPdfServerUrl">>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] =
    useState<Array<PrintHandlerResponse & { label: string }>>();

  const onSubmit = useCallback(async () => {
    try {
      const requests = ensureArray(onRequestPrint?.());
      if (!requests.length) return;

      setIsSubmitting(true);
      setResults(undefined);

      const resultsInternal = await Bluebird.map(requests, async (request) => {
        const result = await printHandler(request, {
          devPdfServerUrl: flags.devPdfServerUrl,
        });
        return { ...result, label: request.label ?? "PDF" };
      });

      setResults(resultsInternal);
      setIsSubmitting(false);
    } catch (error) {
      assertNativeError(error);
      alert(error.message);
      setIsSubmitting(false);
    }
  }, [onRequestPrint, flags.devPdfServerUrl]);

  return (
    <Modal
      title="Print"
      size="small"
      onAfterClose={() => {
        setResults(undefined);
        setIsSubmitting(false);
      }}
      buttons={
        <SubmitButtons
          onCancel={props.onRequestClose}
          isSubmitting={isSubmitting}
          isSubmitDisabled={requestPrintDisabled || onRequestPrint == null}
          submitLabel={"Generate"}
          submitIcon={flags.devPdfServerUrl && <IconTestTubeLine />}
          onSubmit={onSubmit}
        />
      }
      {...props}
    >
      {(!!children || results?.length) && (
        <PaddedBody>
          <VSpace>
            {children}
            <ButtonGroup>
              {results?.map((result, i) => {
                if (!result.success) {
                  console.error(result);
                  return (
                    <Callout key={i} icon={<IconWarningTriangleLine />}>
                      {result.label} failed to generate.
                    </Callout>
                  );
                }
                return (
                  <a href={result.url} target="_blank" rel="noreferrer" key={i}>
                    <Button
                      title={`Download ${result.label}`}
                      icon={<IconDownloadLine />}
                    >
                      Download {result.label}
                    </Button>
                  </a>
                );
              })}
            </ButtonGroup>
          </VSpace>
        </PaddedBody>
      )}
    </Modal>
  );
};

export default PrintModal;
